import { Component, OnInit, OnDestroy, OnChanges, SimpleChanges, Input, Output, EventEmitter, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormGroup, FormBuilder, Validators } from '@angular/forms';
import { Observable, Subject, combineLatest } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { InputForm } from 'ustore-ui';

import { CardWrapperComponent } from '../../../shared/components/card-wrapper/card-wrapper.component';
import { LocationModalComponent, LocationData } from '../../../shared/components/location-modal/location-modal.component';
import { LoadingComponent } from '../../../shared/components/loading/loading.component';
import { UserFacade } from '../../../core/store/user/user.facade';
import { LocationFacade } from '../../../core/store/location/location.facade';
import { User, UserAddress } from '../../../core/models/user.model';
import { formatZipCode } from '../../../core/utils/utils';
import { unformatCnpj } from '../../../core/utils/cnpj.util';
import { I18nPipe } from '../../../shared/pipes/i18n.pipe';
import { MindHpsService, MindHpItem } from '../../../core/services/mind-hps.service';

const COMPLEMENT_MANUAL = '__manual__';

@Component({
  selector: 'umkt-installation-address-card',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    CardWrapperComponent,
    LocationModalComponent,
    LoadingComponent,
    I18nPipe,
    InputForm,
  ],
  templateUrl: './installation-address-card.component.html',
  styleUrl: './installation-address-card.component.scss'
})
export class InstallationAddressCardComponent implements OnInit, OnDestroy, OnChanges {
  @Input() canGoToNextStep = false;
  @Input() isCardLoading = false;
  @Input() isEditing = false;
  @Input() isLastStep = false;
  @Input() fetchMindHps = false;
  @Input() savedAddressDetails: { complement: string; reference: string } | null = null;
  @Output() nextStep = new EventEmitter<void>();
  @Output() previousStep = new EventEmitter<void>();
  @Output() editingStateChange = new EventEmitter<boolean>();
  @Output() addressDetailsSubmit = new EventEmitter<{
    complement: string;
    reference: string;
    homePass: string | null;
    installationAddress: { zipcode: string; number: string };
  }>();

  userInformation$: Observable<User | null>;
  isLoading$: Observable<boolean>;
  isFormValid = false;
  userWithInstallAddress: any = null;
  addressForm!: FormGroup;
  isLocationModalOpen = false;

  hpsOptions: MindHpItem[] = [];
  loadingHps = false;
 
  selectedComplementOption: string = '';
  selectedHp: MindHpItem | null = null;

  readonly COMPLEMENT_MANUAL = COMPLEMENT_MANUAL;
  formatZipCode = formatZipCode;

  private destroy$ = new Subject<void>();
  private userFacade = inject(UserFacade);
  private locationFacade = inject(LocationFacade);
  private fb = inject(FormBuilder);
  private mindHpsService = inject(MindHpsService);

  constructor() {
    this.userInformation$ = this.userFacade.getUserDetails();
    this.isLoading$ = this.userFacade.getIsLoading();

    this.addressForm = this.fb.group({
      complement: ['', Validators.required],
      reference: ['']
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['savedAddressDetails']) {
      this.updateFormValidity();
    }
    if (
      changes['fetchMindHps'] &&
      changes['fetchMindHps'].currentValue === true &&
      changes['fetchMindHps'].previousValue === false &&
      this.userWithInstallAddress
    ) {
      this.loadHps();
    }
  }

  ngOnInit(): void {
    this.userInformation$.pipe(take(1)).subscribe(user => {
      if (!user) {
        this.userFacade.setLoading(true);
        this.userFacade
          .getUserUuid()
          .pipe(take(1))
          .subscribe(uuid => {
            if (uuid) {
              this.userFacade.loadUserDetails(uuid);
            } else {
              this.userFacade.setLoading(false);
            }
          });
      }
    });

    combineLatest([
      this.userInformation$,
      this.locationFacade.location$
    ]).pipe(takeUntil(this.destroy$)).subscribe(([user, installationLocation]) => {
      if (user) {
        const fromUser = user.installAddress ?? this.installAddressFromFirstAddress(user.addresses);
        const fromStore = installationLocation;
        this.userWithInstallAddress = {
          ...user,
          installAddress: {
            address: (fromStore?.logradouro ?? fromUser?.address) ?? '',
            number: fromStore?.addressNumber ?? fromUser?.number ?? '',
            neighborhood: (fromStore?.neighborhood ?? fromUser?.neighborhood) ?? '',
            city: fromStore?.city ?? fromUser?.city ?? '',
            state: fromStore?.state ?? fromUser?.state ?? '',
            zipCode: fromStore?.cep ?? fromUser?.zipCode ?? '',
            complement: fromUser?.complement ?? '',
            reference: (fromUser as any)?.reference ?? ''
          }
        };
        this.addressForm.patchValue({
          complement: this.userWithInstallAddress.installAddress.complement || '',
          reference: (this.userWithInstallAddress.installAddress as any).reference ?? ''
        });
        this.updateFormValidity();
        if (this.fetchMindHps) {
          this.loadHps();
        } else {
          this.loadingHps = false;
          this.hpsOptions = [];
          this.selectedComplementOption = COMPLEMENT_MANUAL;
          this.selectedHp = null;
          this.updateComplementValidator();
        }
      }
    });

    this.addressForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.updateFormValidity());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private installAddressFromFirstAddress(addresses: UserAddress[] | undefined): Partial<{ address: string; number: number | string; neighborhood: string; city: string; state: string; zipCode: string; complement: string }> | null {
    const first = addresses?.[0];
    if (!first) return null;
    return {
      address: first.address ?? '',
      number: first.number ? parseInt(first.number, 10) || first.number : '',
      neighborhood: first.neighborhood ?? '',
      city: first.city ?? '',
      state: first.state ?? '',
      zipCode: first.zipCode ?? '',
      complement: first.complement ?? ''
    };
  }

  private getCepAndNumber(): { cep: string; number: string } | null {
    const addr = this.userWithInstallAddress?.installAddress;
    if (!addr?.zipCode) return null;
    const number = addr.number != null && addr.number !== '' ? String(addr.number) : '';
    if (!number) return null;
    return { cep: String(addr.zipCode).replace(/\D/g, ''), number };
  }

  loadHps(): void {
    const params = this.getCepAndNumber();
    if (!params) {
      this.hpsOptions = [];
      this.updateComplementValidator();
      return;
    }
    this.loadingHps = true;
    this.hpsOptions = [];
    this.selectedComplementOption = '';
    this.selectedHp = null;
    this.mindHpsService
      .getHps(params.cep, params.number)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (items) => {
          this.hpsOptions = items || [];
          this.loadingHps = false;
          if (this.hpsOptions.length === 0) {
            this.selectedComplementOption = COMPLEMENT_MANUAL;
          }
          this.updateComplementValidator();
          this.syncComplementSelectionFromForm();
        },
        error: () => {
          this.loadingHps = false;
          this.hpsOptions = [];
          this.selectedComplementOption = COMPLEMENT_MANUAL;
          this.selectedHp = null;
          this.updateComplementValidator();
          this.syncComplementSelectionFromForm();
        }
      });
  }

  private syncComplementSelectionFromForm(): void {
    const complement = (this.addressForm.get('complement')?.value ?? '').toString().trim();
    if (!this.hpsOptions.length) {
      this.selectedHp = null;
      return;
    }
    if (complement && this.hpsOptions.some(h => h.complement === complement)) {
      this.selectedComplementOption = complement;
      this.selectedHp = this.hpsOptions.find(h => h.complement === complement) ?? null;
      return;
    }
    this.selectedComplementOption = '';
    this.selectedHp = null;
  }

  private updateComplementValidator(): void {
    const complementCtrl = this.addressForm.get('complement');
    if (!complementCtrl) return;
    if (this.hpsOptions.length === 0) {
      complementCtrl.clearValidators();
    } else {
      complementCtrl.setValidators(Validators.required);
    }
    complementCtrl.updateValueAndValidity();
    this.updateFormValidity();
  }

  onComplementOptionChange(value: string): void {
    this.selectedComplementOption = value;
    if (value === COMPLEMENT_MANUAL) {
      this.addressForm.patchValue({ complement: '' }, { emitEvent: true });
      this.selectedHp = null;
      return;
    }
    const item = this.hpsOptions.find(h => h.complement === value);
    this.selectedHp = item ?? null;
    this.addressForm.patchValue({ complement: value || '' }, { emitEvent: true });
  }

  private updateFormValidity(): void {
    const addr = this.userWithInstallAddress?.installAddress;
    const hasAddress = !!(addr?.address && addr?.zipCode && (addr?.number != null && addr?.number !== ''));
    const formComplement = (this.addressForm.get('complement')?.value ?? '').toString().trim();
    const savedComplement = (this.savedAddressDetails?.complement ?? addr?.complement ?? '').toString().trim();
    const hasComplement =
      this.hpsOptions.length === 0 ||
      !!formComplement ||
      !!savedComplement;
    this.isFormValid = !!(hasAddress && hasComplement);
  }

  openLocationModal(): void {
    this.isLocationModalOpen = true;
  }

  closeLocationModal(): void {
    this.isLocationModalOpen = false;
  }

  onLocationConfirm(locationData: LocationData): void {
    this.locationFacade.setLocation({
      city: locationData.city ?? '',
      state: locationData.state ?? '',
      cnpj: locationData.cnpj ? unformatCnpj(locationData.cnpj) : undefined,
      cep: locationData.cep?.replace(/\D/g, '') || undefined,
      addressNumber: locationData.addressNumber ?? undefined,
      logradouro: locationData.logradouro,
      neighborhood: locationData.neighborhood
    });
    if (this.userWithInstallAddress) {
      this.userWithInstallAddress.installAddress = {
        ...this.userWithInstallAddress.installAddress,
        address: locationData.logradouro || '',
        number: parseInt(locationData.addressNumber, 10) || 0,
        neighborhood: locationData.neighborhood || '',
        city: locationData.city || '',
        state: locationData.state || '',
        zipCode: locationData.cep
      };
      this.addressForm.patchValue({
        complement: '',
        reference: this.addressForm.get('reference')?.value ?? ''
      });
      this.selectedComplementOption = '';
      this.selectedHp = null;
      this.loadHps();
    }
    this.isLocationModalOpen = false;
  }

  getDisplayComplement(): string {
    return (this.savedAddressDetails?.complement ?? this.userWithInstallAddress?.installAddress?.complement ?? '').toString();
  }

  getDisplayReference(): string {
    return (this.savedAddressDetails?.reference ?? (this.userWithInstallAddress?.installAddress as any)?.reference ?? '').toString();
  }

  editAddress(): void {
    this.addressForm.patchValue({
      complement: this.getDisplayComplement(),
      reference: this.getDisplayReference()
    }, { emitEvent: true });
    this.syncComplementSelectionFromForm();
    this.isEditing = true;
    this.editingStateChange.emit(this.isEditing);
  }

  cancelEdit(): void {
    this.isEditing = false;
    this.editingStateChange.emit(this.isEditing);
  }

  saveAndContinue(): void {
    const complement = (this.addressForm.get('complement')?.value ?? '').toString();
    const reference = (this.addressForm.get('reference')?.value ?? '').toString();
    const homePass =
      this.selectedHp != null ? String(this.selectedHp.hp) : null;
    const addr = this.userWithInstallAddress?.installAddress;
    if (addr) {
      this.userWithInstallAddress.installAddress = {
        ...addr,
        complement,
        reference
      };
    }
    const zipcode = (addr?.zipCode ?? '').toString().replace(/\D/g, '');
    const number =
      addr?.number != null && addr?.number !== ''
        ? String(addr.number)
        : '';
    this.addressDetailsSubmit.emit({
      complement,
      reference,
      homePass,
      installationAddress: { zipcode, number },
    });
    if (this.isLastStep) {
      this.isEditing = false;
      this.editingStateChange.emit(this.isEditing);
    } else {
      this.nextStep.emit();
    }
  }
}
